// "Próximo passo" — o bloco que os CRMs de referência põem no topo do negócio
// (o "Foco" do Pipedrive): a próxima atividade em aberto, com data, e um botão
// para concluir ou agendar outra. Quando não há nenhuma, o bloco AVISA.
//
// Por que existe (medido em produção, 01/10/2026): 178 dos 181 negócios abertos
// não tinham nenhuma atividade agendada. As atividades já existiam numa aba —
// faltava a tela dizer, em cima, "este negócio não tem próximo passo".
//
// Não cria regra nova: lê e grava pelas mesmas rotas de atividade que a aba
// Atividades usa, com o mesmo vínculo (origem comercial + id do negócio).
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Button, Typography, App as AntApp, Space } from 'antd';
import { PlusOutlined, CheckOutlined, WarningOutlined, CalendarOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { ModalAtividade, type Atividade } from '../Atividades';
import { ehAberto } from '../comercial-pipeline/saudeNegocio';

const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

export function ProximoPassoBloco({ negocio, reloadFlag, onMudou, compacto = false }: {
  negocio: { id: number; titulo: string; status: string; vendedor_email?: string | null };
  reloadFlag?: number;
  /** Avisa o pai que uma atividade foi criada ou concluída. */
  onMudou?: () => void;
  compacto?: boolean;
}) {
  const { message } = AntApp.useApp();
  const [atividades, setAtividades] = useState<Atividade[] | null>(null);
  const [meuEmail, setMeuEmail] = useState('');
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<Atividade | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        setMeuEmail(String((session.tokens?.idToken?.payload as any)?.email || '').toLowerCase());
      } catch { /* sem sessão: o modal cai no padrão dele */ }
    })();
  }, []);

  async function carregar() {
    try {
      const token = await getToken();
      const r = await axios.get(
        `${API_URL}/atividades?escopo=origem&origem_modulo=comercial&origem_id=${negocio.id}`,
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setAtividades(Array.isArray(data) ? data : []);
    } catch {
      // Falha de leitura NÃO vira "sem próximo passo": o bloco diz que não soube.
      setAtividades(null);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [negocio.id, reloadFlag]);

  async function concluir(a: Atividade) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`, { action: 'update', id: a.id, status: 'concluida' },
        { headers: { Authorization: token } });
      message.success('Atividade concluída');
      await carregar();
      onMudou?.();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }

  if (!ehAberto(negocio.status)) return null;

  const abertas = (atividades || [])
    .filter(a => a.status === 'pendente' || a.status === 'em_andamento')
    .sort((a, b) => String(a.data_vencimento || '9999').localeCompare(String(b.data_vencimento || '9999')));
  const proxima = abertas[0] || null;
  const venc = proxima?.data_vencimento ? String(proxima.data_vencimento).slice(0, 10) : '';
  const hoje = hojeIso();
  const atrasada = !!venc && venc < hoje;
  const paraHoje = venc === hoje;

  const cor = atividades === null ? '#9AA0B4' : !proxima ? '#B07500' : atrasada ? '#B91626' : paraHoje ? '#B07500' : '#0930D0';
  const fundo = atividades === null ? '#F7F8FA' : !proxima ? '#FFF8E6' : atrasada ? '#FDF0EF' : '#F4F6FB';

  const presetVendedor = negocio.vendedor_email
    ? [{ email: String(negocio.vendedor_email).toLowerCase(), papel: 'responsavel' }] : undefined;
  const origemPreset = {
    origem_modulo: 'comercial', origem_tipo: 'proposta', origem_id: String(negocio.id),
    origem_label: negocio.titulo || `Negócio #${negocio.id}`,
    ...(presetVendedor ? { atribuicoes: presetVendedor } : {}),
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: fundo,
      border: `1px solid ${cor}33`, borderLeft: `3px solid ${cor}`, borderRadius: 6,
      padding: compacto ? '8px 10px' : '10px 14px', marginBottom: compacto ? 10 : 14 }}>
      <div style={{ flex: '1 1 220px', minWidth: 0 }}>
        <Typography.Text style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.07em', textTransform: 'uppercase', color: cor }}>
          Próximo passo
        </Typography.Text>
        {atividades === null ? (
          <div style={{ fontSize: 13, color: '#5A6378' }}>Não consegui ler as atividades deste negócio agora.</div>
        ) : !proxima ? (
          <div style={{ fontSize: 13, color: '#0E1433' }}>
            <WarningOutlined style={{ color: cor, marginRight: 6 }} />
            Nenhuma atividade agendada. Negócio sem próximo passo tende a parar.
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#0E1433', minWidth: 0 }}>
            <button type="button" onClick={() => setEditando(proxima)}
              style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
                fontWeight: 600, color: '#0E1433', textAlign: 'left' }}>
              {proxima.titulo}
            </button>
            {/* A data também abre a atividade (14/10/2026): "sem data" é onde a pessoa clica para pôr uma. */}
            <button type="button" onClick={() => setEditando(proxima)} title={!venc ? 'Definir a data' : 'Mudar a data'}
              style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
                color: cor, marginLeft: 8, fontWeight: atrasada || paraHoje ? 600 : 400, whiteSpace: 'nowrap' }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {!venc ? 'sem data — definir' : atrasada ? `atrasada desde ${dataBr(venc)}` : paraHoje ? 'para hoje' : dataBr(venc)}
            </button>
            {abertas.length > 1 && (
              <span style={{ color: '#5A6378', marginLeft: 8, fontSize: 12 }}>+{abertas.length - 1} em aberto</span>
            )}
          </div>
        )}
      </div>
      <Space size={6} wrap>
        {proxima && (
          <Button size="small" icon={<CheckOutlined />} onClick={() => concluir(proxima)}>Concluir</Button>
        )}
        <Button size="small" type={proxima ? 'default' : 'primary'} icon={<PlusOutlined />} onClick={() => setNovo(true)}>
          {proxima ? 'Agendar outro' : 'Agendar próximo passo'}
        </Button>
      </Space>

      {(novo || editando) && (
        <ModalAtividade
          atividade={editando}
          meuEmail={meuEmail}
          origemPreset={origemPreset as any}
          onClose={() => { setNovo(false); setEditando(null); }}
          onSalvo={async () => { setNovo(false); setEditando(null); await carregar(); onMudou?.(); }}
        />
      )}
    </div>
  );
}
